import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";

const MotionBox = motion.create(Box);

const CustomerReviewCarousel = ({ reviews }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const handlePrev = () => {
    setDirection(-1);
    setIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setDirection(1);
    setIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const interval = setInterval(handleNext, 6000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  const variants = {
    enter: (dir) => ({
      opacity: 0,
      x: dir > 0 ? 100 : -100,
      scale: 0.95,
    }),
    center: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.4 },
    },
    exit: (dir) => ({
      opacity: 0,
      x: dir > 0 ? -100 : 100,
      scale: 0.95,
      transition: { duration: 0.3 },
    }),
  };

  if (!reviews || reviews.length === 0) {
    return (
      <Box sx={{ mx: "auto" }}>
        <Typography variant="h6" color="text.secondary">
          No reviews available at the moment.
        </Typography>
      </Box>
    );
  }

  const { image, customerName, description, packageName } = reviews[index];

  return (
    <Box
      sx={{
        p: 2,
        position: "relative",
        overflow: 'hidden',
        height: { xs: 520, md: 360 }
      }}
    >
      <AnimatePresence mode="wait" custom={direction}>
        <MotionBox
          key={index}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          sx={{ width: "100%" }}
        >
          <Paper
            elevation={4}
            sx={{
              maxWidth: 900,
              borderRadius: 4,
              mx: 'auto',
              p: 2,
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }}>
              {/* Left: Image */}
              <img
                src={image}
                alt={packageName}
                style={{
                  width: 300,
                  height: 300,
                  alignSelf: 'center'
                }}
              />

              {/* Right: Text */}
              <Stack spacing={1} sx={{ justifyContent: "center", flex: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  {customerName}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    display: "inline-block",
                    backgroundColor: "#e3f2fd",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: "0.75rem",
                    mt: 0.5,
                    alignSelf: "center",
                  }}
                >
                  {packageName}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    mt: 2,
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                  }}
                >
                  “{description}”
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </MotionBox>
      </AnimatePresence>

      {/* Controls */}
      <IconButton
        onClick={handlePrev}
        sx={{
          position: "absolute",
          top: "50%",
          left: 12,
          transform: "translateY(-50%)",
          zIndex: 2,
          backgroundColor: "white",
        }}
      >
        <ChevronLeft />
      </IconButton>
      <IconButton
        onClick={handleNext}
        sx={{
          position: "absolute",
          top: "50%",
          right: 12,
          transform: "translateY(-50%)",
          zIndex: 2,
          backgroundColor: "white",
        }}
      >
        <ChevronRight />
      </IconButton>
    </Box>
  );
};

export default CustomerReviewCarousel;
